/**
 * Utilities for ProtectWise QueryInput & Query Syntax
 */
import _ from 'lodash'
import {
  convert
} from './util/luceneQueryGenerator'
import {
  IDE_PART_TYPES
} from 'pw-drafty'
import {
  getEnumValidator
} from 'pw-validators'
import {
  parseQuery
} from './queryParser'
import {
  CASE_INSENSITIVE,
  QUERY_INPUT_TYPES,
  QUERY_INPUT_SCHEMA_DEFAULTS,
  QUERY_INPUT_TYPE_NAMES
} from './constants'


/**
 * QueryStateSchema utils
*/

function _convertQuerySchemaPartToEditorSchemaPart (querySchemaPart, querySchemaPartFieldName) {
  const part = _.cloneDeep(querySchemaPart)

  if (part.options) {
    part.optionsToOutput = {}  // Store case-sensitive original enums for reverse translation
    part.queryOptions = []  // Store query IDs for validation
    part.displayOptions = []  // Store query IDs for validation
    part.options = _.map(part.options, opt => {
      const _optDisplayStr = typeof opt === 'string' ? opt : opt.name
      const _optQueryStr = typeof opt === 'string' ? opt : opt.id
      const _displayOpt = CASE_INSENSITIVE ? _optDisplayStr.toLowerCase() : _optDisplayStr
      part.displayOptions.push(_optDisplayStr)
      part.optionsToOutput[_displayOpt] = _optQueryStr
      part.queryOptions.push(_optQueryStr)
      return _displayOpt
    })
  }
  part._originalLabel = part.label
  part.label = CASE_INSENSITIVE ? part.label.toLowerCase() : part.label

  const dataTypeDef = QUERY_INPUT_TYPES[querySchemaPart.type]

  part.validate = part.validate || dataTypeDef.validate.bind(part)
  part.validateOperator = part.validateOperator || _validateOperator.bind(part)
  // part.options = part.valueOptions // QueryInput enum matches are case-insensitive
  part.operatorOptions = part.operatorOptions || dataTypeDef.operatorOptions

  if (!part.hasOwnProperty('isQueryOption')) {
    part.isQueryOption = true // Expose key as typeaheadOption
  }
  part.className = (part.className || "") + ` rule_clause clause_${ querySchemaPartFieldName }`
  part.partType = IDE_PART_TYPES.KEY_VALUE
  part.children = part.children || {}
  part.children.key = _.assign(part.children.key || {}, {
    className: `${part.className} query_part_input_type_${querySchemaPart.type.toLowerCase()}`,
    tooltip: `Testing key tooltips, ${ querySchemaPartFieldName }`,
  })
  part.children.operator = _.assign(part.children.operator || {}, {
    className: `${part.className} query_part_input_type_${querySchemaPart.type.toLowerCase()}`,
    tooltip: `Testing operator tooltips`,
    // options: part.operatorOptions, // QueryInput enum matches are case-insensitive
    // validate: _validateOperator.bind(part)
  })
  part.children.value = _.assign(part.children.value || {}, {
    className: `${part.className} query_part_input_type_${querySchemaPart.type.toLowerCase()}`,
    tooltip: `Testing value tooltips, ${ querySchemaPartFieldName }`,
  })
  return part
}


const _validateOperator = getEnumValidator('operatorOptions')

export function convertQuerySchemaToEditorSchema (querySchema) {
  const convertedSchema = {}

  for (var i = 0; i < querySchema.length; i++) {
    const schemaPart = querySchema[i]
    const schemaKey = CASE_INSENSITIVE ? schemaPart.label.toLowerCase() : schemaPart.label
    convertedSchema[schemaKey] = _convertQuerySchemaPartToEditorSchemaPart(schemaPart, schemaPart.fieldName)
  }

  return _.assign({
    _schemaUpdatedAt: Date.now() // QueryInput will read this value when determining if it needs to refresh its internal cache of typeahead options
  }, QUERY_INPUT_SCHEMA_DEFAULTS, convertedSchema)
}




/**
 * QueryState -> Lucene utils
*/

function _objectWithKey(key, value) {
  const _out = []
  _out[key] = _.isArray(value) ? value.map(v => `${ v }`) : (_.isNumber(value) ? `${ value }` : value )
  return _out
}

function _convertSpecialClauseToLucene(specialClauseType, fieldName, queryPart) {
  switch (specialClauseType) {
    case 'bitSet':
      return `{!bits field=${fieldName} values=${Array.isArray(queryPart.bits) ? queryPart.bits.join(',') : queryPart.bits }}`
    case 'geo':
      if (queryPart.withinRadius) {
        return `{!geofilt sfield=${fieldName} pt=${queryPart.withinRadius.point.lat},${queryPart.withinRadius.point.lon} d=${queryPart.withinRadius.distance}}`
      }
      else if (queryPart.boundingBox) {
        return _objectWithKey(fieldName, {
          $from: `${ queryPart.boundingBox.lowerLeft.lat },${ queryPart.boundingBox.lowerLeft.lon }`,
          $to: `${ queryPart.boundingBox.upperRight.lat },${ queryPart.boundingBox.upperRight.lon }`
        })
      }
      else {
        throw new Error(`pw-query queryUtils: _convertSpecialClauseToLucene(specialClauseType:geo) : Unrecognized query format`)
      }
    default:
      throw new Error(`pw-query queryUtils: _convertSpecialClauseToLucene: Invalid specialClauseType: ${specialClauseType}`)
  }
}

function _recurDescendJsQueryTree (queryDepth) {
  const out = []
  for (var i = 0; i < queryDepth.length; i++) {
    const queryPart = queryDepth[i]
    if (queryPart.or) {
      out.push({
        $operator: 'or',
        $operands: _recurDescendJsQueryTree(queryPart.or)
      })
    }
    else if (queryPart.and) {
      out.push({
        $operator: 'and',
        $operands: _recurDescendJsQueryTree(queryPart.and)
      })
    }
    else if (queryPart.not) {
      out.push({
        $operator: 'not',
        $operands: _recurDescendJsQueryTree([queryPart.not])
      })
    }
    else if (queryPart.op) {
      const {
        name,
        op
      } = queryPart

      if (op === '!eq' || op === 'eq' || op === 'in') {
        const operand = _objectWithKey(name, queryPart.value)
        out.push(op === '!eq' ? {
          $operator: 'not',
          $operands: [operand]
        } : operand)
      }
      else if (op === 'lte' || op === 'lt') {
        out.push(_objectWithKey(name, {
          $from: "*",
          $to: `${ op === 'lt' ? +queryPart.value - 1 : queryPart.value }`
        }))
      }
      else if (op === 'gte' || op === 'gt') {
        out.push(_objectWithKey(name, {
          $from: `${ op === 'gt' ? +queryPart.value + 1 : queryPart.value }`,
          $to: "*",
        }))
      }
      else if (op === 'between') {
        out.push(_objectWithKey(name, {
          $from: `${ queryPart.from }`,
          $to: `${ queryPart.to }`
        }))
      }
      else if (op === 'timerange') {
        out.push(_objectWithKey(name, {
          $from: `${ queryPart.from }`,
          $to: `${ queryPart.to }`
        }))
      }
      else if (op === 'bitSet') {
        out.push(_convertSpecialClauseToLucene('bitSet', name, queryPart))
      }
      else if (op === 'geo') {
        out.push(_convertSpecialClauseToLucene('geo', name, queryPart))
      }
      else {
        console.warn('Invalid operator converting query -> lucene: ', queryPart, op)
      }
    }
    else {
      console.warn('Invalid part converting query -> lucene: ', queryPart)
    }
  }
  return out
}


/*
EXPORTED FUNCTIONS
*/


export function getQueryStats (jsQuery) {
  let fields = []
  let depth = 0
  for (let i = 0, iLen = jsQuery.length; i < iLen; i++) {
    const clause = jsQuery[i]
    if (clause.or) {
      fields = fields.concat(getQueryStats(clause.or).fieldNames)
      depth += 1
    }
    else if (clause.and) {
      fields = fields.concat(getQueryStats(clause.and).fieldNames)
      depth += 1
    }
    else if (clause.not) {
      fields = fields.concat(getQueryStats(clause.not).fieldNames)
      depth += 1
    }
    else {
      fields.push(clause.name)
    }
  }
  return {
    fieldNames: _.uniq(fields),
    depth
  }
}

/**
 * sugar around `convertQueryJsToLucene` that handles intermediate parsing, and throws if there is a syntax error
 * 
 * @param {string} query UI-formatted query
 * @param {object} querySchema QueryInput schema
 * 
 * @returns {string} luceneQueryString
 */
export function parseQueryToLucene (query, querySchema) {
  const parsedQuery = parseQueryString(query, querySchema)
  if (!parsedQuery.IDEStateData || parsedQuery.IDEStateData.invalid) {
    throw new Error('parseQueryToLucene invalid pwQuery or parsing failed')
  } else {
    const converterSchema = generateConverterSchema(querySchema)
    return convertQueryJsToLucene(parsedQuery.IDEStateData.queryState, converterSchema)
  }
}

const generateConverterSchema = querySchema => Object.values(querySchema).reduce((out, schemaPart) => {
  // Any QUERY_TYPES that could contain a string should be passed here
  if (schemaPart.type === QUERY_INPUT_TYPE_NAMES.STRING || schemaPart.type === QUERY_INPUT_TYPE_NAMES.ENUM) {
    out[schemaPart.fieldName] = {
      type: 'string',
      allowSpecialCharacters: true // Needed to avoid "/"blah"/" double escaping of quotes
    }
  }
  return out
},
{}
)


export function convertQueryJsToLucene (jsQuery, converterSchema, escapeFieldNames=true) {
  const converterOptions = {
    schema: converterSchema,
    escapeKeys: escapeFieldNames
  }

  const preppedForLuceneGenerator = _recurDescendJsQueryTree(jsQuery)

  const converted = convert({
    $operands: preppedForLuceneGenerator
  }, converterOptions)
  return converted.replace(/("\*")/g, "*")
}

export function convertQuerySyntaxToLucene(pwQuery, schema, options) {
  if (!pwQuery) {
    return pwQuery
  }
  const parsed = parseQueryString(pwQuery, schema, options)
  if (!parsed.IDEStateData || parsed.IDEStateData.invalid) {
    console.warn('convertQuerySyntaxToLucene invalid pwQuery or parsing failed')
    return pwQuery
  }
  return convertQueryJsToLucene(parsed.IDEStateData.queryState, options.luceneConverterSchema || {})
}

export function parseQueryString (inputString = "", schema, {allowUnknownFields, validate, caretOffset = -1, optionMaps = {}} = {}) {
  const {
    queryState,
    editorState,
    validationMessages,
    typeaheadState
  } = parseQuery(inputString, caretOffset, schema, allowUnknownFields, optionMaps)

  // Handle post-parsing validation function if supplied
  if (validate) {
    // Function may mutate any of these variables
    validate(editorState, queryState, validationMessages)
  }

  return {
    IDEState: editorState,
    IDEStateData: {
      queryState,
      invalid: validationMessages.length > 0,
      invalidSummary: validationMessages
    },
    typeaheadState
  }
}
